import { replace } from 'redux-first-router';

import { unique } from '../utils/array';
import { prefix, translate } from '../utils/locale';
import { deepClone } from '../utils/object';

const initialState = {
	categories: [],
	filterCategories: [],
	colors: [],
	finishes: [],
	filteredBy: [],
	currentFilterSegment: '', // Specific brand or top category.
	clearFilterBase: [], // Clicked link set when force used. Should be updated if removed in filter. Women / Sale clicked -> Sale removed in filter. Women only remains in clearFilterBase.
	sort: '',
};

const updateFilteredBy = (state, newFilterKey, newFilter) => {
	if ((typeof newFilter[newFilterKey] === 'boolean' && newFilter[newFilterKey])) {
		return [...state, newFilter];
	}

	let removeIndex = false;
	let updated = false;

	// Check if value exists -> update
	const tempFilter = state.map((currentFilter, i) => {
		const prevFilterKey = Object.keys(currentFilter)[0];
		if (newFilterKey === prevFilterKey) {
			if ((Array.isArray(newFilter[newFilterKey]) && newFilter[newFilterKey].length === 0) || newFilter[newFilterKey] === false) {
				removeIndex = i;
			}
			updated = true;
			return newFilter;
		}
		return currentFilter;
	});

	// Remove
	if (removeIndex !== false) {
		return [...state.slice(0, removeIndex), ...state.slice(removeIndex + 1)];
	}

	// Add
	if (updated === false) {
		return [...state, newFilter];
	}

	// Update
	return tempFilter;
};

const updateFilterSegment = (state, filterKey, filterValue = '') => {
	if (state.currentFilterSegment === '' && (filterKey === 'categories' || filterKey === 'brands' || filterKey === 'productsOnSale' || filterKey === 'newProducts')) {
		state = {
			...state,
			currentFilterSegment: filterKey === 'categories' ? filterValue : 'all',
		};
	}

	return state;
};

const updateFilterState = (state, filterKey, filterValue, force) => {
	let updatedValues = [];

	if (!state[filterKey]) return state;

	if (force && state[filterKey].indexOf(filterKey) !== -1) {
		const value = state[filterKey];
		updatedValues = unique([...value.slice(value.indexOf(filterValue), value.indexOf(filterValue) + 1), ...value.slice(0, value.indexOf(filterValue)), ...value.slice(value.indexOf(filterValue) + 1)]); // Move value to front.
	} else if (!force && state[filterKey].indexOf(filterValue) !== -1) {
		updatedValues = unique([...state[filterKey].slice(0, state[filterKey].indexOf(filterValue)), ...state[filterKey].slice(state[filterKey].indexOf(filterValue) + 1)]); // Remove
	} else if (force) {
		updatedValues = unique([filterValue, ...state[filterKey]]); // Add value
	} else {
		updatedValues = unique([...state[filterKey], filterValue]); // Add value
	}

	state = {
		...state,
		[filterKey]: updatedValues,
		filteredBy: updateFilteredBy(state.filteredBy, filterKey, { [filterKey]: [...updatedValues] }),
	};

	if (force || !state.clearFilterBase) {
		state = {
			...state,
			clearFilterBase: updateFilteredBy(state.clearFilterBase, filterKey, { [filterKey]: [...updatedValues] }),
		};
	}

	state = { ...updateFilterSegment(state, filterKey, filterValue) };

	return state;
};

const filterNextState = (state, action) => {
	let nextState = deepClone(state);

	// [{ filterKey: filterValue ([], '') }, { ... }, ...]

	action.filterArray.forEach((filter) => {
		const filterKey = Object.keys(filter)[0];
		let filterValueRaw = filter[filterKey];

		if (filterKey === 'categories') {
			if (!Array.isArray(filterValueRaw)) filterValueRaw = [filterValueRaw];

			filterValueRaw.forEach((filterValue) => {
				const categoryParts = filterValue.split('/');
				if (nextState.categories.length === 1 && nextState.categories.indexOf(filterValue) !== -1 && !action.force) {
					/*
						// Removing final category.
						kids/clothing/gifts -> kids/clothing/gifts => kids/clothing (moved up one level - can't have no categories unless there is another base)
					*/
					const newValue = categoryParts.length === 1 ? [filterValue] : [categoryParts.slice(0, -1).join('/')];

					if (nextState.currentFilterSegment === 'all' && categoryParts.length === 1) {
						nextState = {
							...nextState,
							[filterKey]: [],
							filteredBy: updateFilteredBy(nextState.filteredBy, filterKey, { [filterKey]: [] }),
							clearFilterBase: updateFilteredBy(nextState.clearFilterBase, filterKey, { [filterKey]: [] }),
						};
					} else {
						nextState = {
							...nextState,
							[filterKey]: newValue,
							filteredBy: updateFilteredBy(nextState.filteredBy, filterKey, { [filterKey]: [...newValue] }),
							clearFilterBase: updateFilteredBy(nextState.clearFilterBase, filterKey, { [filterKey]: [...newValue] }),
						};
					}
				} else if (nextState.categories.length > 0) {
					/*
						// Filter when choosing deeper categories i.e.
						kids -> kids/clothing/gifts => kids/clothing/gifts (removes 'kids')
						kids/shoes,kids/clothing -> kids/clothing/gifts => kids/shoes,kids/clothing/gifts (removes 'kids/clothing')

						// Parent category added remove superfluous deeper categories
						kids/shoes,kids/clothing -> kids => kids (removes kids/shoes and kids/clothing as they exist in 'kids')
					*/

					const newValues = nextState.categories.filter((previousCategory) => {
						// if categories are exactly the same then remove them.
						if (previousCategory === filterValue && !action.force) return false;
						if (previousCategory === filterValue && action.force) return true;

						const previousCategoryParts = previousCategory.split('/');

						// if previous categories are more general than filterValues and have same base. Remove the more generic categories.
						if (previousCategoryParts.length < categoryParts.length) {
							// check if they have same base.
							let hasSameBase = true;
							categoryParts.slice(0, -1).forEach((part, i) => {
								if (previousCategoryParts[i] !== undefined && previousCategoryParts[i] !== part) hasSameBase = false;
							});

							// Has same base previous category is less specific than new category. Remove the old category.
							if (hasSameBase) return false;
						}

						// if previous categories are more specific than filterValue and have same base. Remove the more specific categories.
						if (previousCategoryParts.length > categoryParts.length) {
							// check if they have same base.
							let hasSameBase = true;
							categoryParts.forEach((part, i) => {
								if (previousCategoryParts[i] !== part) hasSameBase = false;
							});

							// Has same base previous category is more specific than new category. Remove the old category.
							if (hasSameBase) return false;
						}

						return true;
					});

					let newCategories = [];

					if (action.force) {
						newCategories = nextState.categories.indexOf(filterValue) === -1 ? [filterValue, ...newValues] : [...newValues.slice(newValues.indexOf(filterValue), newValues.indexOf(filterValue) + 1), ...newValues.slice(0, newValues.indexOf(filterValue)), ...newValues.slice(newValues.indexOf(filterValue) + 1)]; // Move value to front.
					} else {
						newCategories = nextState.categories.indexOf(filterValue) === -1 ? [...newValues, filterValue] : [...newValues];
					}

					nextState = {
						...nextState,
						[filterKey]: newCategories,
						filteredBy: updateFilteredBy(nextState.filteredBy, filterKey, { [filterKey]: [...newCategories] }),
					};
				} else {
					nextState = updateFilterState(nextState, filterKey, filterValue, action.force);
				}
			});
		} else if (typeof nextState[filterKey] === 'boolean') {
			// filterValueRaw is only string for boolean.
			nextState = updateFilterSegment(nextState, filterKey);

			nextState = {
				...nextState,
				[filterKey]: action.force ? true : !nextState[filterKey],
				filteredBy: updateFilteredBy(nextState.filteredBy, filterKey, { [filterKey]: action.force ? true : !nextState[filterKey] }),
				clearFilterBase: action.force ? updateFilteredBy(nextState.clearFilterBase, filterKey, { [filterKey]: action.force ? true : !nextState[filterKey] }) : [...state.clearFilterBase],
			};
		} else if (filterKey === 'sort') {
			// filterValueRaw is only string for sort.
			nextState = {
				...nextState,
				sort: nextState.sort === filterValueRaw ? '' : filterValueRaw,
			};
		} else {
			if (!Array.isArray(filterValueRaw)) filterValueRaw = [filterValueRaw];

			filterValueRaw.forEach((filterValue) => {
				nextState = updateFilterState(nextState, filterKey, filterValue, action.force);
			});
		}
	});

	return nextState;
};

const updateURL = (filter, setup, currentPath) => {
	if (!filter || filter.filteredBy.length === 0) return;

	const campaignHash = {
		newProducts: 'filterNewUri',
		productsOnSale: 'filterSaleUri',
	};
	let filterURI = prefix(setup, currentPath);
	let filterCampaign = '';
	let filterHash = '';

	// Check if we are on a brands page...
	filter.filteredBy.forEach((filterSegment, i) => {
		const filterKey = Object.keys(filterSegment)[0];
		const filterValue = Array.isArray(filterSegment[filterKey]) ? [...filterSegment[filterKey]] : filterSegment[filterKey];

		if (i === 0) {
			if (filterKey === 'categories' && filterValue.length > 0) {
				filterURI = prefix(setup, filterValue.shift());
			} else if (filterKey === 'brands' && filterValue.length > 0) {
				filterURI = prefix(setup, `${currentPath.split('/').slice(0, 1).join('/')}/${filterValue.shift()}`);
			} else if (typeof filterValue === 'boolean' && filterValue) {
				filterURI = prefix(setup, currentPath.split('/').slice(0, 1).join('/'));
			}
		} else if (filter.currentFilterSegment === 'all' && filterKey === 'categories') {
			// Category not main filter, take first category to add to path.
			filterURI += `/${filterValue.shift()}`;
		} else if (typeof filterValue === 'boolean' && filterValue) {
			// If filtered with a campaign append campaign don't allow prepending of campaigns
			// Need to get the translated version.
			filterCampaign = `/${translate(setup, campaignHash[filterKey])}`;
		}

		if (Array.isArray(filterValue) && filterValue.length > 0) {
			filterHash += `${filterKey}=${filterValue.join(',')}&`;
		} else if (typeof filterValue !== 'boolean' && filterValue.length > 0) {
			// Not used here. For values that aren't arrays.
			filterHash += `${filterKey}=${filterValue}&`;
		}
	});

	filterURI += filterCampaign;
	filterHash = filterHash.length > 0 ? `#${filterHash.replace(/&$/, '')}` : '';

	if (prefix(setup, currentPath) !== `${filterURI}${filterHash}`) replace(`${filterURI}${filterHash}`);
};

export default (state = deepClone(initialState), action = {}) => {
	if (action.type === 'FILTER') {
		const nextState = filterNextState(state, action);
		updateURL(nextState, action.setup, action.currentPath);
		return nextState;
	}
	if (action.type === 'CLEAR_FILTER') {
		action.force = true;
		action.filterArray = deepClone(state.clearFilterBase);
		return filterNextState(deepClone(initialState), action);
	}
	if (action.type === 'CLEAR_SORT') {
		return filterNextState(state, { filterKey: 'sort', filterArray: [{ sort: '' }] });
	}
	if (action.type === 'RESET_FILTER') {
		const nextState = deepClone(initialState);
		updateURL(nextState, action.setup, action.currentPath);
		return nextState;
	}

	return state;
};
